/**
 * Axios Instance Configuration
 * 統一的 HTTP 請求設定
 */

import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { API_CONFIG } from '@/config'

/**
 * 建立 Axios Instance
 */
export const axiosInstance = axios.create({
  baseURL: `${API_CONFIG.baseURL}/api/v3`,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * 全局 Access Token
 * 透過 setAccessToken 設置，自動加入所有請求
 */
let globalAccessToken: string | null = null

/**
 * 設置全局 Access Token
 * 設置後會自動加入到所有後續的請求中
 */
export const setAccessToken = (token: string | null) => {
  globalAccessToken = token
  console.log('[Axios] Access token updated:', token ? '***' + token.slice(-6) : 'null')
}

/**
 * 獲取當前的 Access Token
 */
export const getAccessToken = () => globalAccessToken

/**
 * 清除 Access Token
 */
export const clearAccessToken = () => {
  globalAccessToken = null
  console.log('[Axios] Access token cleared')
}

/**
 * Request Interceptor
 * 自動在 header 加入 Bearer Token
 */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 如果有全局 token，自動加入到請求 header
    if (globalAccessToken) {
      config.headers.Authorization = `Bearer ${globalAccessToken}`
    }
    return config
  },
  (error: AxiosError) => {
    // eslint-disable-next-line no-console
    console.error('[Axios Request Error]:', error)
    return Promise.reject(error)
  }
)

/**
 * Response Interceptor
 * 統一處理回應和錯誤
 */
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // 成功回應直接返回 data
    return response
  },
  (error: AxiosError) => {
    // 統一錯誤處理
    // eslint-disable-next-line no-console
    console.error('[Axios Response Error]:', error)

    // 自訂錯誤訊息
    let errorMessage = '發生未知錯誤'

    if (error.response) {
      // 伺服器回應錯誤
      const status = error.response.status
      switch (status) {
        case 400:
          errorMessage = '請求參數錯誤'
          break
        case 401:
          errorMessage = '未授權，請重新登入'
          break
        case 403:
          errorMessage = '沒有權限執行此操作'
          break
        case 404:
          errorMessage = '請求的資源不存在'
          break
        case 500:
          errorMessage = '伺服器內部錯誤'
          break
        default:
          errorMessage = `請求失敗 (${status})`
      }
    } else if (error.request) {
      // 請求已發送但沒有收到回應
      errorMessage = '網路連線失敗，請檢查網路狀態'
    } else {
      // 其他錯誤
      errorMessage = error.message || '發生未知錯誤'
    }

    // 返回自訂錯誤
    return Promise.reject(new Error(errorMessage))
  }
)
